const allocationForTable = (order, tableNumber) =>
  order.allocations.find((a) => a.table == tableNumber);

const allocationsAsText = (allocations) =>
  allocations
    .map((a) => `${a.table}:${a.seats}`)
    .sort()
    .join(",");

const orderChanged = (order) =>
  allocationsAsText(order.allocations) !=
  allocationsAsText(order.originalAllocations);

const assignDistinctTableToOrder = (order, tableNumber) => {
  if (allocationForTable(order, tableNumber)) {
    return false;
  }
  order.allocations.push({ table: tableNumber, seats: 0 });

  return true;
};

export const unassignTableFromOrder = (order, tableNumber) => {
  const index = order.allocations.findIndex((a) => a.table == tableNumber);
  if (index > -1) {
    order.allocations.splice(index, 1);
  }
};

export const getAllocatedSeats = (order, tableNumber) =>
  allocationForTable(order, tableNumber)?.seats ?? 0;

export const getNumberOfOrdersForTable = (orders, tableNumber) =>
  orders.filter((o) => o.allocations.some((a) => a.table == tableNumber)).length;

export const getSeatsUsedByOrder = (order) =>
  order.allocations.reduce((total, a) => total + a.seats, 0);

export const getSeatsUsedOnTable = (orders, tableNumber) =>
  orders.reduce((total, o) => total + getAllocatedSeats(o, tableNumber), 0);

export const getUnallocatedSeats = (order, excludedTable) =>
  Math.max(
    order.seatCount -
      (getSeatsUsedByOrder(order) - getAllocatedSeats(order, excludedTable)),
    0
  );

export const getTablesForOrder = (order) =>
  order.allocations.map((a) => a.table);

export const getUpdatedOrders = (orders, migrationRequired) => {
  const updatedOrders = [];

  orders.forEach((o) => {
    if (migrationRequired || orderChanged(o))
      updatedOrders.push({
        Number: o.number,
        NoteSequenceNumber: o.noteSequenceNumber,
        Allocations: o.allocations.map((a) => ({
          Table: a.table,
          Allocation: a.seats,
        })),
      });
  });

  return updatedOrders;
};

export const getUpdatedTables = (tableList, tableStatuses) =>
  tableList
    .filter((t) => t.currentStatus != t.originalStatus)
    .map((t) => ({
      SequenceNumber: t.sequenceNumber,
      TableId: t.tableId,
      Status: t.currentStatus,
      StatusName: tableStatuses[t.currentStatus] ?? "None",
    }));

export const processAssignments = (data) => {
  const assignments = { orders: [], tables: [] };
  assignments.tables = data.tables.map((t) => ({
    tableId: t.tableId,
    capacity: t.capacity,
    sequenceNumber: t.tableSequence,
    originalStatus: t.tableStatus,
    currentStatus: t.tableStatus,
  }));
  data.orders.forEach((o) => {
    const allocations = (o.allocations ?? []).map((a) => ({
      table: a.table,
      seats: a.allocation,
    }));
    assignments.orders.push({
      number: o.number,
      account: o.account,
      contact: o.contact,
      seatCount: o.seatCount,
      noteSequenceNumber: o.noteSequenceNumber,
      allocations,
      originalAllocations: structuredClone(allocations),
    });
  });
  assignments.orders.sort((a, b) =>
    a.account.toLowerCase().localeCompare(b.account.toLowerCase())
  );

  return assignments;
};

export const setAllocatedSeats = (order, tableNumber, seats) => {
  const allocation = allocationForTable(order, tableNumber);
  if (allocation) {
    allocation.seats = seats;
  }
};

export const setRemainingSeats = (order, tableNumber) =>
  setAllocatedSeats(order, tableNumber, getUnallocatedSeats(order, tableNumber));

export const setOrdersForTable = (orders, tableNumber, orderNumbers) => {
  const assignedOrders = [];
  orders.forEach((o) => {
    if (orderNumbers.some((n) => n == o.number)) {
      if (assignDistinctTableToOrder(o, tableNumber)) {
        assignedOrders.push(o);
      }
    } else {
      unassignTableFromOrder(o, tableNumber);
    }
  });

  return assignedOrders;
};

export const setTablesForOrder = (order, tables) => {
  const assignedTables = tables.filter((t) =>
    assignDistinctTableToOrder(order, t)
  );
  order.allocations
    .filter((a) => !tables.some((t) => t == a.table))
    .forEach((a) => unassignTableFromOrder(order, a.table));

  return assignedTables;
};
